import { ServiceAddressSchema, type ServiceAddress } from '@rheora/enrollment-contract';

/**
 * Serviceability form logic (E-SF03) — kept OUT of the `.tsx` per CLAUDE.md.
 * Validation reuses the shared contract schema so the FE and BFF agree on what a
 * well-formed address is; presentation helpers map domain enums to i18n keys.
 */

export interface AddressForm {
  readonly line1: string;
  readonly unit: string;
  readonly city: string;
  readonly zip: string;
}

export const emptyAddressForm: AddressForm = { line1: '', unit: '', city: '', zip: '' };

export type AddressFormErrors = Partial<Record<keyof AddressForm, string>>;

export interface AddressFormValidation {
  readonly ok: boolean;
  readonly errors: AddressFormErrors;
  readonly value: ServiceAddress | undefined;
}

/**
 * Validate the raw form into a `ServiceAddress`. Field errors are i18n KEYS, not
 * copy — the component resolves them through `t()`.
 */
export function validateAddressForm(form: AddressForm): AddressFormValidation {
  const errors: AddressFormErrors = {};
  if (form.line1.trim().length === 0) errors.line1 = 'shop.address.form.line1.required';
  if (form.city.trim().length === 0) errors.city = 'shop.address.form.city.required';
  if (!/^\d{5}$/u.test(form.zip.trim())) errors.zip = 'shop.address.form.zip.invalid';

  if (Object.keys(errors).length > 0) return { ok: false, errors, value: undefined };

  const candidate = {
    line1: form.line1.trim(),
    ...(form.unit.trim().length > 0 ? { unit: form.unit.trim() } : {}),
    city: form.city.trim(),
    zip: form.zip.trim(),
  };
  const parsed = ServiceAddressSchema.safeParse(candidate);
  // Belt-and-braces: the field checks above should already guarantee validity.
  if (!parsed.success) return { ok: false, errors: { zip: 'shop.address.form.zip.invalid' }, value: undefined };
  return { ok: true, errors: {}, value: parsed.data };
}

/** Minimal email shape check for the lead form (the BFF re-validates). */
export function isValidEmail(email: string): boolean {
  const trimmed = email.trim();
  if (trimmed.length === 0 || trimmed.length > 254) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(trimmed);
}

/** i18n key for a switch-vs-move-in badge. */
export function enrollmentTypeLabelKey(type: 'SWITCH' | 'MOVE_IN'): string {
  return type === 'SWITCH' ? 'shop.address.enroll.switch' : 'shop.address.enroll.moveIn';
}

/** i18n key for the plan-availability note; null when not shopping a plan. */
export function planAvailabilityKey(planAvailable: boolean | null): string | null {
  if (planAvailable === null) return null;
  return planAvailable ? 'shop.address.plan.available' : 'shop.address.plan.unavailable';
}
